import { APP_CONSTANTS } from 'src/consumer/constants'

import CalendarMonth from '@mui/icons-material/CalendarMonth'
import CalendarViewDay from '@mui/icons-material/CalendarViewDay'
import EventAvailable from '@mui/icons-material/EventAvailable'
import Home from '@mui/icons-material/Home'
import LiveHelp from '@mui/icons-material/LiveHelp'
import Lock from '@mui/icons-material/Lock'
import Policy from '@mui/icons-material/Policy'
import Settings from '@mui/icons-material/Settings'
import LocalHospital from '@mui/icons-material/LocalHospital'
import ManageAccounts from '@mui/icons-material/ManageAccounts'
import { Drawer, IconButton, Popover, Tooltip, useMediaQuery, useTheme } from '@mui/material'
import MenuIcon from '@mui/icons-material/Menu'
import { useContext, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import kycIconSvg from 'src/assets/kyc approval.svg'
import { MentorProfileContext } from 'src/mentor/context/MentorProfileContext'
import { getAllRolesInBoolean, getIsTeacherOrCreatorData } from 'src/mentor/layout/utils'
import {
  KycApprovalHeading,
  KycSidebarPendingContent,
  SidebarBrandText,
  SidebarContainer,
  SidebarIconWrap,
  SidebarItemContainer,
  SidebarItemText,
  SidebarKycIcon,
  SidebarNavButton,
  SidebarNavGroup,
  SidebarTop,
  StyledLogo,
} from './MentorSidebarStyles'
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet'
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong'
import SummarizeIcon from '@mui/icons-material/Summarize'

export default function MentorSidebar({ associatedClinicBrands }) {
  const navigate = useNavigate()
  const location = useLocation()
  const { mentorProfileData, allLegalPolicies, mentorClinicAssociationData } =
    useContext(MentorProfileContext)
  const offersLive = mentorProfileData?.services?.live
  const relevantRolesData = getIsTeacherOrCreatorData()
  const roles = getAllRolesInBoolean()

  // Support enables the owner business surfaces per brand (ClinicBrand.ownerPortalSettings).
  // Default is OFF: the entry shows only when a loaded brand has a surface explicitly
  // enabled — on routes where associations don't load it stays hidden (the page and the
  // backend both enforce the flags anyway).
  const ownerBrands = associatedClinicBrands
  const anyOwnerBusinessSurfaceEnabled =
    Array.isArray(ownerBrands) &&
    ownerBrands.some((brand) => {
      const settings = brand?.ownerPortalSettings
      return (
        settings?.showBusinessModels === true ||
        settings?.showPayoutReports === true ||
        settings?.showPayoutInvoices === true
      )
    })
  const [popoverAnchor, setPopoverAnchor] = useState(null)
  const theme = useTheme()
  // Below the sm breakpoint (phones) the fixed rail is replaced by a hamburger + slide-in drawer so
  // the page content can use the full width.
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const [mobileOpen, setMobileOpen] = useState(false)

  function handleOpenPopover(event) {
    mentorProfileData?.kycStatus === 'pending' && setPopoverAnchor(event.currentTarget)
  }
  function handleClosePopover() {
    setPopoverAnchor(null)
  }

  function isActivePath(path) {
    if (path === '/mentor-home') {
      return location.pathname === path || location.pathname === `${path}/`
    }

    return location.pathname === path || location.pathname.startsWith(`${path}/`)
  }

  function handleNavigate(item) {
    if (item.disabled) {
      return
    }

    setMobileOpen(false)
    navigate(item.path)
  }

  const navItems = [
    {
      label: 'Home',
      path: '/mentor-home',
      icon: Home,
      disabled: mentorProfileData?.kycStatus === 'pending',
      show: true,
      showsKycPopover: true,
    },
    {
      label: 'Clinic Bookings',
      path: '/mentor-home/my-clinic-associations',
      icon: LocalHospital,
      show: mentorClinicAssociationData && mentorClinicAssociationData?.length > 0,
    },
    {
      label: 'Upcoming Appointments',
      path: '/mentor-home/upcoming-appointments',
      icon: EventAvailable,
      show: true,
    },
    {
      label: 'Calendar Settings',
      path: '/mentor-home/my-calendar',
      icon: CalendarMonth,
      show: offersLive && !relevantRolesData?.isCreator && relevantRolesData?.isTeacher,
    },
    {
      label: 'Prescription Pads',
      path: '/mentor-home/prescription-templates',
      icon: SummarizeIcon,
      show: roles.isDoctor && mentorProfileData?.allowedToPrescribe,
    },
    {
      label: 'Clinic Fee Collection',
      path: '/mentor-home/fee-collection-report',
      icon: AccountBalanceWalletIcon,
      show: roles.isDoctor,
    },
    {
      label: 'Business Model & Invoices',
      path: '/mentor-home/business-and-invoices',
      icon: ReceiptLongIcon,
      show: roles.isClinicOwner && anyOwnerBusinessSurfaceEnabled,
    },
    {
      label: 'Homework Plans',
      path: '/mentor-home/homework-plans',
      icon: CalendarViewDay,
      show:
        mentorProfileData?.services?.recorded &&
        mentorProfileData?.contentCategories?.includes('activity-plan'),
    },
    {
      label: 'FAQs',
      path: '/mentor-home/faqs',
      icon: LiveHelp,
      show: true,
    },
    {
      label: 'Agreements & Banking',
      path: '/mentor-home/agreements',
      icon: Policy,
      disabled: mentorProfileData?.kycStatus === 'pending',
      show: allLegalPolicies?.length > 0,
      showsKycPopover: true,
    },
    {
      label: 'EMR Settings',
      path: '/mentor-home/settings',
      icon: Settings,
      show: roles.isDoctor,
    },
    {
      label: 'Profile Settings',
      path: '/mentor-home/profile',
      icon: ManageAccounts,
      show: true,
    },
  ].filter((item) => item.show)

  const sidebarContent = (
    <>
      <SidebarTop
        onClick={() => {
          setMobileOpen(false)
          navigate('/mentor-home')
        }}
      >
        <StyledLogo
          src={APP_CONSTANTS.APP_LOGO_LINK}
          onError={(e) => {
            e.target.src = '/logo_brand.png'
          }}
        />
        <SidebarBrandText>
          <strong>AUMHUM</strong>
          <span>Mentor portal</span>
        </SidebarBrandText>
      </SidebarTop>

      <SidebarNavGroup aria-label="Mentor navigation">
        {navItems.map((item) => {
          const IconComponent = item.icon
          const active = isActivePath(item.path)

          return (
            <Tooltip key={item.path} title={item.label} placement="right" arrow enterDelay={350}>
              <SidebarNavButton
                type="button"
                $active={active}
                $disabled={item.disabled}
                aria-disabled={item.disabled ? 'true' : undefined}
                aria-current={active ? 'page' : undefined}
                onMouseEnter={item.showsKycPopover ? handleOpenPopover : undefined}
                onMouseLeave={item.showsKycPopover ? handleClosePopover : undefined}
                onClick={() => handleNavigate(item)}
              >
                <SidebarIconWrap $active={active} $disabled={item.disabled}>
                  <IconComponent sx={{ fontSize: 21 }} />
                </SidebarIconWrap>
                <SidebarItemContainer>
                  <SidebarItemText $active={active} $disabled={item.disabled}>
                    {item.label}
                  </SidebarItemText>
                  {item.disabled && <Lock sx={{ fontSize: 16, color: '#94a3b8' }} />}
                </SidebarItemContainer>
              </SidebarNavButton>
            </Tooltip>
          )
        })}
      </SidebarNavGroup>

      <Popover
        open={Boolean(popoverAnchor)}
        anchorEl={popoverAnchor}
        sx={{
          pointerEvents: 'none',
        }}
        PaperProps={{ sx: { borderRadius: '15px' } }}
        onClose={handleClosePopover}
        anchorOrigin={{
          vertical: 'center',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'center',
          horizontal: 'left',
        }}
        disableRestoreFocus
      >
        <KycSidebarPendingContent>
          <SidebarKycIcon 
            src={kycIconSvg} 
            alt="KYC Approval Required"
            loading="lazy"
            decoding="async"
          />
          <KycApprovalHeading>
            To access this section<br></br>
            your KYC needs to be approved
          </KycApprovalHeading>
        </KycSidebarPendingContent>
      </Popover>
    </>
  )

  // Phones: hamburger button + slide-in drawer (content is full-width; see ContentContainer).
  if (isMobile) {
    return (
      <>
        <IconButton
          aria-label="Open navigation menu"
          onClick={() => setMobileOpen(true)}
          className="hide-on-print-only"
          sx={{
            position: 'fixed',
            top: 8,
            left: 8,
            zIndex: 1150,
            bgcolor: 'rgba(255, 255, 255, 0.96)',
            border: '1px solid rgba(203, 213, 225, 0.72)',
            boxShadow: '0 2px 8px rgba(15, 23, 42, 0.12)',
            '&:hover': { bgcolor: '#fff' },
          }}
        >
          <MenuIcon sx={{ color: '#334155' }} />
        </IconButton>
        <Drawer
          anchor="left"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          PaperProps={{ sx: { border: 'none' } }}
        >
          <SidebarContainer
            className="hide-on-print-only"
            style={{ position: 'static', minHeight: '100vh', boxShadow: 'none' }}
          >
            {sidebarContent}
          </SidebarContainer>
        </Drawer>
      </>
    )
  }

  return <SidebarContainer className="hide-on-print-only">{sidebarContent}</SidebarContainer>
}
